/* eslint-disable no-undef */
/*
  All methods below check for cordova & the existence of the plugin,
  upon failure they will error out, which is to be handled by the consumer.

  Upon success, most methods do NOT return anything.
*/
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import {
  calculateDistanceBetweenTwoPointsSphericalLawOfCosines,
  calculateBearing,
  totalTimeBetweenTwoTimestamps,
} from '@deckee/utilities';
import BackgroundGeolocation from '@transistorsoft/capacitor-background-geolocation';
import { BackgroundFetch } from '@transistorsoft/capacitor-background-fetch';
import { telemetry } from '../../backend';
import { get, getStatic, set } from '../../local-storage';
import { getStore } from '../../store';
import { actions } from '../../store/modules/log-trips';
import { emit, definitions } from '../../message-broker';
import {
  getDeviceId,
  getSessionId,
} from '../../event-capture/anonymous-data-capture';
import {
  moveMapToLocation,
  selectLocationConfig,
} from '../../store/modules/app-controls';
import { getMemberSignalSharingSettings } from '../../store/modules/app-controls/actions';
import { updateCurrentHeading } from '../../store/modules/geo-service';
import { hasFeatureEnabled } from '../../feature';
import checkCapacitorAndPluginsAvailable from '../../helper-functions';
import { captureEvent } from '../../event-capture';
import {
  loadRemoteTripIntoActiveTrip,
  requestPermissions,
  setBackgroundGeolocationDisclosed,
} from '..';

/**
 * This is used to set extras within the headers to be sent with each point.
* @summary Note that this MUST be applied before starting a trip as the tripID
  is recorded with the point at the time of creation, not thereafter.
* @param {string} tripID - ID to be associated with the trip in the database,
*/
const configureBackgroundGeolocationHeaders = checkCapacitorAndPluginsAvailable(
  ['BackgroundGeolocation', 'BackgroundFetch'],
  async ({ lowFrequencyTracking }) => {
    const extras = {};
    const currentTripID = await get('logTrips', 'currentTripID');
    extras.tripID = currentTripID;
    const deviceId = await getDeviceId();
    extras.deviceId = deviceId;
    const sessionId = getSessionId();
    extras.sessionId = sessionId;
    const signalSharingSettings = getMemberSignalSharingSettings();
    extras.signalSharing = signalSharingSettings;
    extras.lowFrequencyTracking = lowFrequencyTracking;
    extras.disableLocationAuthorizationAlert = true;
    await BackgroundGeolocation.setConfig({ extras });
  },
);

/**
* Force a sync with a local database (SQLite) and the remote server.
* @summary Helpful for use if we detect that the local database is filling up
  due to being unable to post to the server.
*/
export const forceSync = checkCapacitorAndPluginsAvailable(
  ['BackgroundGeolocation', 'BackgroundFetch'],
  async () => {
    await BackgroundGeolocation.sync();
  },
);

export const backgroundGeolocationForceStop = checkCapacitorAndPluginsAvailable(
  ['BackgroundGeolocation', 'BackgroundFetch'],
  async () => {
    // eslint-disable-next-line no-async-promise-executor
    // const retry = async (attempts = 60) => new Promise(async (resolve, reject) => {
    //   if (attempts < 0) reject(Error('error'));
    //   try {
    //     await forceSync();
    //     resolve();
    //   } catch (err) {
    //     setTimeout(() => {
    //       retry(attempts - 1).then(resolve, reject);
    //     }, 1000);
    //   }
    // });
    // await retry();
    await forceSync();
    await BackgroundGeolocation.stopSchedule();
    await BackgroundGeolocation.stop();
  },
);

/**
 * Update local state with position data
 * @param {object} location - Location object sourced from the background-geolocation plugin
 */
const updatePosition = async ({ location }) => {
  const currentTrip = (await get('logTrips', 'currentTrip')) || [];
  // Location is formatted like this for use as a GeoJSON line string
  const formattedLocation = [
    location?.coords?.longitude,
    location?.coords?.latitude,
  ];
  currentTrip.push(formattedLocation);

  const lastRecordedPosition =
    (await get('logTrips', 'lastRecordedPosition')) || {};
  await set('logTrips', 'prevRecordedPosition', lastRecordedPosition);

  await set('logTrips', 'currentTrip', currentTrip);
  await set('logTrips', 'lastRecordedPosition', location);
};

/**
 * Remove current trip from local storage (intended for use in displaying).
 */
export const resetCurrentTrip = () => {
  set('logTrips', 'currentTrip', []);
  set('logTrips', 'lastRecordedPosition', null);
  set('logTrips', 'prevRecordedPosition', null);
  set('logTrips', 'totalDistance', 0);
  emit(definitions.map.logTripStatusChange, { status: 'reset' });
};

const providerChangeHandler = (event) => {
  captureEvent({
    type: 'settings: Location Services',
    payload: {
      isEnable: event.enabled,
    },
  });

  if (event.enabled && event.gps) {
    getStore().dispatch(actions.setGpsAvailable());
  } else {
    getStore().dispatch(actions.clearGpsAvailable());
  }
};

/**
 * One-shot check of provider state so we can set the initial GPS status.
 * https://transistorsoft.github.io/cordova-background-geolocation-lt/classes/backgroundgeolocation.html#onproviderchange
 * https://transistorsoft.github.io/cordova-background-geolocation-lt/classes/backgroundgeolocation.html#getproviderstate
 * https://transistorsoft.github.io/cordova-background-geolocation-lt/interfaces/providerchangeevent.html
 */
export const checkProviderState = checkCapacitorAndPluginsAvailable(
  ['BackgroundGeolocation', 'BackgroundFetch'],
  async () => {
    // Force retrieves the current state of location-provider authorization before beginning to listen
    try {
      const providerState = await BackgroundGeolocation.getProviderState();
      providerChangeHandler(providerState);
    } catch (error) {
      telemetry(
        telemetry.ERROR,
        `[logTrips background-geolocation] getProviderState received error of ${JSON.stringify(
          error,
        )}`,
      );
    }
  },
);

/**
* This is used to configure the headers of the POST requests made by the plugin to
  send location to the remote server.
* @summary This is used in the initialize to set up the api-key in the header to be
  the clientToken, but if the clientToken ever needs to change this method should be used.
* @param {object} headers - HTTP headers for POST requests
*/
export const configureBackgroundGeolocationSyncHeaders =
  checkCapacitorAndPluginsAvailable(
    ['BackgroundGeolocation', 'BackgroundFetch'],
    async (headers) => {
      await BackgroundGeolocation.setConfig({ headers });
    },
  );

/**
 * Append options setting into BackgroundGeolocation
 * @summary This is used in the append configuration settings to the Background Geolocation service
 * @param {object} options
 */
export const updateBackgroundGeolocationConfiguration =
  checkCapacitorAndPluginsAvailable(
    ['BackgroundGeolocation', 'BackgroundFetch'],
    async (options) => {
      await BackgroundGeolocation.setConfig({ ...options });
    },
  );

/**
 * Total distance calculation to show on front end during a trip
 * @param {object} location - Location object sourced from the background-geolocation plugin
 * @return {object} Object containing current total distance and the distance between last two points
 */
const updateTotalDistance = ({ location }) => {
  let newDistance = 0;
  let currentTotalDistance = getStatic('logTrips', 'totalDistance') || 0;
  const lastRecordedPosition =
    getStatic('logTrips', 'lastRecordedPosition') || null;

  if (
    lastRecordedPosition?.coords?.latitude &&
    lastRecordedPosition?.coords?.longitude &&
    location?.coords?.latitude &&
    location?.coords?.longitude
  ) {
    newDistance = calculateDistanceBetweenTwoPointsSphericalLawOfCosines(
      lastRecordedPosition.coords.latitude,
      lastRecordedPosition.coords.longitude,
      location?.coords?.latitude,
      location?.coords?.longitude,
    );
    currentTotalDistance += newDistance;
  }

  set('logTrips', 'lastRecordedDistance', newDistance);
  set('logTrips', 'totalDistance', currentTotalDistance);
  return {
    currentTotalDistance,
    distanceBetweenLastTwoPoints: newDistance,
  };
};

/**
 * Get the plugins current state (note this includes ALL current configuration)
 */
export const getBackgroundGeolocationState = checkCapacitorAndPluginsAvailable(
  ['BackgroundGeolocation', 'BackgroundFetch'],
  async () => BackgroundGeolocation.getState(),
);

// handlers
const activityChangeHandler = async (event) => {
  // If activity changes to be still no new locations will be recorded
  // Thus set trip statistics for speed and heading o be unknown as they are not moving
  if (event?.activity === 'still') {
    const currentTotalDistance = (await get('logTrips', 'totalDistance')) || 0;
    getStore().dispatch(
      actions.setCurrentTripStatistics(null, currentTotalDistance),
    );
  }
};

function shouldIncludeWhenGps(location) {
  return location?.coords?.accuracy >= 0 && location?.coords?.accuracy < 30;
}

function shouldIncludeWhenNotGps(location) {
  const locationAccuracy = location?.coords?.accuracy;
  return locationAccuracy < 10 && locationAccuracy >= 0;
}

/**
 * Get the heading bearing to display on front end
 * @param {float} currentHeading - Current heading as provided from GPS
 * @param {object} currentLocation - Location as last received from GPS
 * @param {boolean} currentlyMoving - Whether the user is moving or not
 * @param {object} prevRecordedPosition - Previous location as last received from GPS
 * @return {float} Current bearing to display on front end
 */
export function getDisplayedHeading({
  currentHeading,
  currentlyMoving,
  currentLocation,
  prevRecordedPosition,
}) {
  // There shouldn't be a heading if you're not moving
  if (!currentlyMoving) {
    return null;
  }

  // Calculate a heading if we're moving and there isn't a valid current heading
  if (currentHeading === null || currentHeading === -1) {
    const derivedHeading = calculateBearing(
      prevRecordedPosition?.coords?.latitude,
      prevRecordedPosition?.coords?.longitude,
      currentLocation?.coords?.latitude,
      currentLocation?.coords?.longitude,
    );
    return Number.isNaN(derivedHeading) ? null : derivedHeading;
  }

  return currentHeading;
}

/**
 * Get the moving average of the latest speeds
 * @param {float} speedBetweenLastTwoPoints - Speed in m/s between the last two points
 * @return {float} The moving average of up to the last 10 speeds in m/s
 */
const getMovingAverageSpeed = ({ speedBetweenLastTwoPoints }) => {
  // Calculate moving average of instantaneous speed up to a window of 10 to match low pass filter on entity store
  // See procedures/data/2022-04-27-speed-smoothing for justification of window of 10 points
  // https://www.notion.so/deckee/Top-speed-is-wrong-when-logging-a-trip-51253425b7cc438caf7ed5e837728d18#a4903e4659694c68a50404410670216d
  const latestSpeeds = getStatic('logTrips', 'latestSpeeds') || [];

  const maximumWindowOfSpeeds =
    window?.DECKEE_CONFIG?.logTrips?.instantaneousSpeedWindow ?? 3;

  if (latestSpeeds.length >= maximumWindowOfSpeeds) {
    // Remove the earliest point
    latestSpeeds.shift();
  }

  latestSpeeds.push(speedBetweenLastTwoPoints);
  set('logTrips', 'latestSpeeds', latestSpeeds);
  return (
    latestSpeeds.reduce((total, next) => total + next, 0) / latestSpeeds.length
  );
};

/**
 * Get the speed value to display on front end
 * @param {float} currentSpeed - Speed as recorded from GPS
 * @param {boolean} currentlyMoving - Whether the user is moving or not
 * @param {object} currentLocation - Location as last received from GPS
 * @param {object} prevRecordedPosition - Previous location as last received from GPS
 * @param {float} distanceBetweenLastTwoPoints - Distance in meters between last two points
 * @return {float} Speed value in m/s to show on front end
 */
function getDisplayedSpeed({
  currentSpeed,
  currentlyMoving,
  currentLocation,
  prevRecordedPosition,
  distanceBetweenLastTwoPoints,
}) {
  // There shouldn't be a heading if you're not moving
  if (!currentlyMoving) {
    return null;
  }

  if (prevRecordedPosition) {
    const timeBetweenLastTwoPoints = totalTimeBetweenTwoTimestamps(
      prevRecordedPosition?.timestamp,
      currentLocation?.timestamp,
    );
    const speedBetweenLastTwoPoints =
      distanceBetweenLastTwoPoints / timeBetweenLastTwoPoints;

    if (hasFeatureEnabled('instantaneous-speed-smoothing')) {
      // See procedures/data/2022-04-27-speed-smoothing for justification on time between last point and next point
      // 99% of points have an average of 18.6 seconds and median of 4.8 seconds between points
      // 25 seconds is used as a safety factor of 25% applied to the average and rounded to nearest 5
      const maximumDurationBetweenLastTwoPoints = 25;
      if (timeBetweenLastTwoPoints <= maximumDurationBetweenLastTwoPoints) {
        return getMovingAverageSpeed({
          speedBetweenLastTwoPoints,
        });
      }
      // If we are outside that with the next point, reset the`current window
      set('logTrips', 'latestSpeeds', []);
    }

    // Calculate a speed from the last 2 points if we're moving and there isn't a valid current speed
    if (currentSpeed === null || currentSpeed === -1) {
      return speedBetweenLastTwoPoints;
    }
  }

  // If failed to compute the speed, return what was provided by the GPS
  return currentSpeed;
}

const locationHandler = async (location) => {
  const isGPS = location.coords.heading !== -1 && location.coords.speed !== -1;

  const isAccurateLocation = isGPS
    ? shouldIncludeWhenGps(location)
    : shouldIncludeWhenNotGps(location);

  // Check for tripID in extras here, as not to log points when getCurrentPosition called.
  if (
    location &&
    !location?.sample &&
    location?.extras?.tripID &&
    isAccurateLocation
  ) {
    const currentlyMoving = location?.is_moving || false;
    const currentHeading = location?.coords?.heading || null;
    const currentSpeed = location?.coords?.speed || null;
    let updatedMapBearing = null;

    const { followModeActive, followMode, tripPaused } =
      await getStore().getState().logTrips;

    const prevRecordedPosition =
      (await get('logTrips', 'prevRecordedPosition')) || null;

    // Update the local state
    updatePosition({ location });
    const { currentTotalDistance, distanceBetweenLastTwoPoints } =
      updateTotalDistance({ location });

    // Get our display values for the current heading and speed
    const currentHeadingToDisplay = getDisplayedHeading({
      currentHeading,
      currentlyMoving,
      currentLocation: location,
      prevRecordedPosition,
    });
    const currentSpeedToDisplay = getDisplayedSpeed({
      currentSpeed,
      currentlyMoving,
      currentLocation: location,
      prevRecordedPosition,
      distanceBetweenLastTwoPoints,
    });

    // Update the store
    await getStore().dispatch(updateCurrentHeading(currentHeadingToDisplay));
    await getStore().dispatch(
      actions.setCurrentTripStatistics(
        currentSpeedToDisplay,
        currentTotalDistance,
      ),
    );

    // only change map bearing and move the map center if follow mode is active
    if (
      followModeActive &&
      !tripPaused &&
      !hasFeatureEnabled('device-orientation')
    ) {
      updatedMapBearing = currentHeading;
      if (followMode === 'north-follow') updatedMapBearing = 0;

      await getStore().dispatch(
        moveMapToLocation({
          bearing: updatedMapBearing,
        }),
      );
    }
  }
};

const locationErrorHandler = (error) => {
  telemetry(
    telemetry.ERROR,
    `[logTrips background-geolocation] onLocation received error of ${JSON.stringify(
      error,
    )}`,
  );
};

/**
 * Get current position
 */
export const backgroundGeolocationGetCurrentPosition =
  checkCapacitorAndPluginsAvailable(
    ['BackgroundGeolocation', 'BackgroundFetch'],
    async () => {
      // Options are mimicked from: containers/app-main-client/src/services/geo/index.js
      // Desired accuracy and samples are from the documentation: https://transistorsoft.github.io/cordova-background-geolocation-lt/classes/backgroundgeolocation.html#getcurrentposition
      const currentLocation = await BackgroundGeolocation.getCurrentPosition({
        maximumAge: 0,
        timeout: 10,
        desiredAccuracy: 10,
        samples: 3,
        persist: true,
      });
      return currentLocation;
    },
  );

// eslint-disable-next-line no-unused-vars
const heartbeatHandler = async (event) => {
  // The Location provided by the HeartbeatEvent is only the last-known location.
  // The heartbeat event does not actively engage location-services.
  // If you wish to get the current location in your callback, use getCurrentPosition.

  // By fetching location here, we are expecting it to capture a location and send it to the server
  // This will then follow the event router to update the OTW state for the user
  // We are then hoping to determine whether the redux state gets updated in the background
  await backgroundGeolocationGetCurrentPosition();
  await forceSync();
};

const setBackgroundGeolocationUrl = async () => {
  const { hostname, protocol } = window.location;
  const clientToken = await get('backendService', 'clientToken');
  // set some params here to help ensure they change when switching environments
  // and when we want to change them.
  // we only need to do this if the reset is false when calling ready.
  await BackgroundGeolocation.setConfig({
    url: `${protocol}//${hostname}/public/app-main/api/log-track-point`,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'api-key': clientToken,
    },
  });
};

// In memory only: a cold start pauses the trip and restores stop detection.
let isRecordingUntilStopped = false;

/**
 * Settings merged over the high or low frequency settings so a started,
 * resumed or restored trip records until the user stops it or closes the app
 * (see README).
 * @param {boolean} recording - Whether such a trip is recording
 * @return {object} Settings to merge over the high or low frequency settings
 */
export const getRecordUntilStoppedSettings = (recording) => ({
  disableStopDetection: recording,
  pausesLocationUpdatesAutomatically: !recording,
  ...(recording && { stopOnTerminate: true }),
});

const getBackgroundGeolocationSettings = async ({ lowFrequencyTracking }) => {
  const { hostname, protocol } = window.location;
  const clientToken = await get('backendService', 'clientToken');
  telemetry(
    telemetry.DEBUG,
    '[logTrips background-geolocation] get device state',
  );
  const currentState = await BackgroundGeolocation.getState();

  telemetry(
    telemetry.DEBUG,
    '[logTrips background-geolocation] get device state result',
    currentState,
  );
  const { url, extras } = currentState;

  const nonConfigureableSettings = {
    disableLocationAuthorizationAlert: true,
    locationAuthorizationRequest:
      BackgroundGeolocation.LOCATION_AUTHORIZATION_ALWAYS,
    // If production then turn off logs as to save storage space
    logLevel:
      hostname === 'deckee.com'
        ? BackgroundGeolocation.LOG_LEVEL_OFF
        : BackgroundGeolocation.LOG_LEVEL_VERBOSE,
    desiredAccuracy:
      Capacitor.getPlatform() === 'ios'
        ? BackgroundGeolocation.DESIRED_ACCURACY_NAVIGATION
        : BackgroundGeolocation.DESIRED_ACCURACY_HIGH,
    activityType: BackgroundGeolocation.ACTIVITY_TYPE_OTHER_NAVIGATION,
    notification: {
      priority: BackgroundGeolocation.NOTIFICATION_PRIORITY_MIN,
    },
    url: `${protocol}//${hostname}/public/app-main/api/log-track-point`,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'api-key': clientToken,
    },
    // copy extras across if on same host
    extras: (url || '').includes(hostname) ? extras : {},
  };

  const lowFrequencyBackgroundGeolocationSettings = JSON.parse(
    window?.DECKEE_CONFIG?.backgroundGeolocation
      ?.lowFrequencyBackgroundGeolocationSettings,
  );
  const highFrequencyBackgroundGeolocationSettings = JSON.parse(
    window?.DECKEE_CONFIG?.backgroundGeolocation
      ?.highFrequencyBackgroundGeolocationSettings,
  );

  if (lowFrequencyTracking) {
    return {
      ...nonConfigureableSettings,
      ...lowFrequencyBackgroundGeolocationSettings,
      ...getRecordUntilStoppedSettings(isRecordingUntilStopped),
      notification: {
        ...nonConfigureableSettings.notification,
        ...lowFrequencyBackgroundGeolocationSettings.notification,
      },
    };
  }
  // If not requesting low frequency tracking, return high
  return {
    ...nonConfigureableSettings,
    ...highFrequencyBackgroundGeolocationSettings,
    ...getRecordUntilStoppedSettings(isRecordingUntilStopped),
    notification: {
      ...nonConfigureableSettings.notification,
      ...highFrequencyBackgroundGeolocationSettings.notification,
    },
  };
};

const updateBackgroundGeolocationSettings = async ({
  lowFrequencyTracking,
}) => {
  await configureBackgroundGeolocationHeaders({ lowFrequencyTracking });
  const backgroundGeolocationSettings = await getBackgroundGeolocationSettings({
    lowFrequencyTracking,
  });
  await BackgroundGeolocation.setConfig(backgroundGeolocationSettings);
};

export const shouldAdoptServerActiveTrip = (localTripID, activeTripID) => {
  if (activeTripID == null || activeTripID === '') {
    return false;
  }
  // Fill extras only when the device has no trip. Do not replace a local id:
  // the user may have ended the auto-trip and started a manual one while an
  // untagged POST was still in flight.
  return String(localTripID || '') === '';
};

const httpHandler = async (event) => {
  // log-track-point returns whether the user is on the water, and any open trip so
  // background JS can stamp `extras.tripID` without waiting for loadActiveTrip.
  if (!event.success) {
    return;
  }

  const parsedResponse = JSON.parse(event.responseText);
  const onTheWaterStatus = parsedResponse?.data?.water;
  const activeTripID = parsedResponse?.data?.activeTripID;
  const localTripID =
    (await get('logTrips', 'currentTripID')) ||
    getStore()?.getState()?.logTrips?.currentTripID;

  if (shouldAdoptServerActiveTrip(localTripID, activeTripID)) {
    await backgroundGeolocationStart({
      tripID: activeTripID,
      lowFrequencyTracking: onTheWaterStatus !== true,
      force: false,
      // Only in the foreground: in the background keep stop detection, so a
      // silent auto trip still lets GPS stop.
      recordUntilStopped: (await App.getState()).isActive,
    });
    try {
      await loadRemoteTripIntoActiveTrip(activeTripID);
    } catch (error) {
      telemetry(
        telemetry.ERROR,
        `[logTrips background-geolocation] Failed to hydrate auto trip ${activeTripID}: ${error}`,
      );
    }
    return;
  }

  if (onTheWaterStatus === true) {
    await updateBackgroundGeolocationSettings({
      lowFrequencyTracking: false,
    });
  } else if (onTheWaterStatus === false) {
    await updateBackgroundGeolocationSettings({
      lowFrequencyTracking: true,
    });
  }
};

// configuration
const configureBackgroundGeolocation = checkCapacitorAndPluginsAvailable(
  ['BackgroundGeolocation', 'BackgroundFetch'],
  async ({ lowFrequencyTracking }) => {
    try {
      telemetry(
        telemetry.DEBUG,
        '[logTrips background-geolocation] Configuring listeners',
      );

      // Listen to BackgroundGeolocation events
      BackgroundGeolocation.onActivityChange(activityChangeHandler);
      BackgroundGeolocation.onLocation(locationHandler, locationErrorHandler);
      BackgroundGeolocation.onProviderChange(providerChangeHandler);
      if (hasFeatureEnabled('on-the-water-detection')) {
        BackgroundGeolocation.onHeartbeat(heartbeatHandler);
        BackgroundGeolocation.onHttp(httpHandler);
      }

      telemetry(
        telemetry.DEBUG,
        '[logTrips background-geolocation] Running the ready function',
      );

      const backgroundGeolocationSettings =
        await getBackgroundGeolocationSettings({ lowFrequencyTracking });

      // **IMPORTANT**
      // YOU MUST ONLY CALL READY ONCE AND ONLY ONCE, EACH TIME YOUR APP IS LAUNCHED
      await BackgroundGeolocation.ready({
        ...backgroundGeolocationSettings,
      });

      // This is done to ensure url & headers are correct when switching environments
      await setBackgroundGeolocationUrl();

      telemetry(
        telemetry.DEBUG,
        '[logTrips background-geolocation] configured successfully',
      );
    } catch (error) {
      telemetry(
        telemetry.ERROR,
        `[logTrips background-geolocation] received error of ${JSON.stringify(
          error,
        )}`,
      );
      throw error;
    }
  },
);

/**
 * A start that fails after setConfig has already left the native plugin with
 * stop detection off, and start() may have turned it on. Put back the normal
 * settings, and stop the plugin if this start turned it on.
 * @param {object} params
 * @param {boolean} params.lowFrequencyTracking - Tracking mode to restore
 * @param {boolean} params.stop - Whether the plugin was off before the start
 */
const undoRecordUntilStopped = async ({ lowFrequencyTracking, stop }) => {
  isRecordingUntilStopped = false;
  try {
    await updateBackgroundGeolocationSettings({ lowFrequencyTracking });
    if (stop) {
      await BackgroundGeolocation.stop();
    }
  } catch (error) {
    captureEvent({
      type: 'background-geolocation: undo record until stopped error',
      payload: {
        message: error.message,
      },
    });
  }
};

/**
* Start tracking geolocation of device. Must be provided a tripID.
* @summary
  Upon each time a location is recorded:

  - POST this data to the remote server
    - if server responds successfully it is deleted out of the local SQLite database.
  - Append the lat/lon of the position to the currentTrip in
    local storage (intended for use in displaying)
* @param {string} tripID - ID to be associated with the trip in the database,
  returned from the startTrip endpoint
*/
let backgroundGeolocationStartAttempt = 1;
export const backgroundGeolocationStart = checkCapacitorAndPluginsAvailable(
  ['BackgroundGeolocation', 'BackgroundFetch'],
  async ({
    tripID,
    lowFrequencyTracking,
    force,
    recordUntilStopped = false,
  }) => {
    const wasRecordingUntilStopped = isRecordingUntilStopped;
    let wasEnabled;
    try {
      if (recordUntilStopped) {
        wasEnabled = (await BackgroundGeolocation.getState()).enabled;
        isRecordingUntilStopped = true;
      }
      if (tripID) {
        telemetry(
          telemetry.INFO,
          `[logTrips background-geolocation backgroundGeolocationStart] Starting background geolocation with trip: ${tripID}`,
        );
        set('logTrips', 'currentTripID', tripID);
      } else {
        telemetry(
          telemetry.INFO,
          `[logTrips background-geolocation backgroundGeolocationStart] Starting background geolocation without a trip`,
        );
      }
      // Update for high frequency tracking
      await updateBackgroundGeolocationSettings({ lowFrequencyTracking });
      await requestPermissions({ forcePrompt: force });

      if (backgroundGeolocationStartAttempt === 1 || force) {
        captureEvent({
          type: 'background-geolocation: start',
          payload: {
            attempt: backgroundGeolocationStartAttempt,
            force,
            tripID,
            lowFrequencyTracking,
          },
        });
        await BackgroundGeolocation.start();
      } else {
        captureEvent({
          type: 'background-geolocation: start limit exceeded',
          payload: {
            attempt: backgroundGeolocationStartAttempt,
            force,
            tripID,
            lowFrequencyTracking,
          },
        });
      }
      // Also when start() is skipped, if the plugin is on: an adopted trip may
      // find it stationary. Needs the foreground: When In Use cannot start
      // location from background.
      if (
        recordUntilStopped &&
        (await BackgroundGeolocation.getState()).enabled
      ) {
        await BackgroundGeolocation.changePace(true);
      }
      backgroundGeolocationStartAttempt += 1;
    } catch (e) {
      if (recordUntilStopped && !wasRecordingUntilStopped) {
        await undoRecordUntilStopped({
          lowFrequencyTracking,
          stop: wasEnabled === false,
        });
      }
      captureEvent({
        type: 'background-geolocation: start error',
        payload: {
          message: e.message,
        },
      });

      throw e;
    }
  },
);

export const backgroundGeolocationStop = checkCapacitorAndPluginsAvailable(
  ['BackgroundGeolocation', 'BackgroundFetch'],
  async () => {
    const state = getStore().getState();

    const { onTheWaterDetectionEnabled } = selectLocationConfig(state);

    const currentTrip = !!state.logTrips.currentTripID;
    const tripPaused = !!state.logTrips.tripPaused;

    // Check to make sure there are no active trips or the trip isn't paused, and signal is turned off before forcing stop
    if (!currentTrip || tripPaused) {
      isRecordingUntilStopped = false;
      // If using OTW, reset to low frequency tracking here
      if (onTheWaterDetectionEnabled) {
        await updateBackgroundGeolocationSettings({
          lowFrequencyTracking: true,
        });
      } else {
        await backgroundGeolocationForceStop();
      }
    }
  },
);

/**
 * Configure background fetch to ping every 15 minutes
 * (to help keep the app alive)
 */
// eslint-disable-next-line no-unused-vars
const configureBackgroundFetch = checkCapacitorAndPluginsAvailable(
  ['BackgroundGeolocation', 'BackgroundFetch'],
  async () => {
    await BackgroundFetch.configure(
      {
        minimumFetchInterval: 15, // <-- default is 15
        // Android config
        stopOnTerminate: false,
        startOnBoot: true,
        enableHeadless: true,
        requiresCharging: false,
        requiresDeviceIdle: false,
        requiresBatteryNotLow: false,
        requiresStorageNotLow: false,
        requiredNetworkType: BackgroundFetch.NETWORK_TYPE_NONE,
      },
      (taskId) => {
        telemetry(
          telemetry.DEBUG,
          '[BackgroundFetch] - Received event:',
          taskId,
        );
        switch (taskId) {
          case 'com.transistorsoft.customtask':
            break;
          default:
            // Schedule another one-shot
            BackgroundFetch.scheduleTask({
              taskId: 'com.transistorsoft.customtask',
              delay: 5000,
              stopOnTerminate: false,
              enableHeadless: true,
              periodic: false,
              forceAlarmManager: true,
            });
            break;
        }
        BackgroundFetch.finish(taskId);
      },
      (taskId) => {
        telemetry(telemetry.WARN, '[BackgroundFetch] TIMEOUT: ', taskId);
        BackgroundFetch.finish(taskId);
      },
    );

    BackgroundFetch.scheduleTask({
      taskId: 'com.transistorsoft.customtask',
      delay: 10000,
      periodic: false,
      stopOnTerminate: false,
      enableHeadless: true,
      forceAlarmManager: true,
    });
  },
);

/**
 * Get all local storage locations in a GeoJSON linestring format for use with displaying.
 */
export const getLocationsLineString = async () => {
  const locations = (await get('logTrips', 'currentTrip')) || [];

  const geojsonLineString = {
    type: 'geojson',
    data: {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: locations,
      },
    },
  };
  return geojsonLineString;
};

/**
 * Get current device information (eg, OS version, manufacturer, etc)
 */
export const getDeviceInfo = checkCapacitorAndPluginsAvailable(
  ['BackgroundGeolocation', 'BackgroundFetch'],
  async () => {
    const deviceInfo = await BackgroundGeolocation.getDeviceInfo();
    return deviceInfo;
  },
);

/**
 * Email plugin logs for debugging purposes.
 */
export const emailDebuggingLogs = checkCapacitorAndPluginsAvailable(
  ['BackgroundGeolocation', 'BackgroundFetch'],
  async (emailAddress) => {
    await BackgroundGeolocation.logger.emailLog(emailAddress);
  },
);

/**
 * Get current odometer reading.
 */
export const backgroundGeolocationGetOdometer =
  checkCapacitorAndPluginsAvailable(
    ['BackgroundGeolocation', 'BackgroundFetch'],
    async () => {
      const odometerValue = await BackgroundGeolocation.getOdometer();

      return odometerValue;
    },
  );

export const backgroundGeolocationRequestPermission =
  checkCapacitorAndPluginsAvailable(
    ['BackgroundGeolocation', 'BackgroundFetch'],
    async () => {
      try {
        await BackgroundGeolocation.requestPermission();
      } catch (status) {
        // This prevents the app show an error, when we force to show the prompt for second time.
        // https://transistorsoft.github.io/capacitor-background-geolocation/classes/backgroundgeolocation.html#requestpermission
      }
    },
  );

export const checkLocationPermission = checkCapacitorAndPluginsAvailable(
  ['BackgroundGeolocation', 'BackgroundFetch'],
  async () => {
    // AUTHORIZATION_STATUS_NOT_DETERMINED: 0
    // AUTHORIZATION_STATUS_RESTRICTED: 1
    // AUTHORIZATION_STATUS_DENIED: 2
    // AUTHORIZATION_STATUS_ALWAYS: 3
    // AUTHORIZATION_STATUS_WHEN_IN_USE: 4

    const { status } = await BackgroundGeolocation.getProviderState();

    if (status === undefined) {
      throw new Error('Location authorization status could not be determined');
    }

    return status === BackgroundGeolocation?.AUTHORIZATION_STATUS_ALWAYS;
  },
);

export const isBackgroundGeolocationDisclosed = async () => {
  if (!window || !Capacitor.isNativePlatform()) {
    return false;
  }
  const isDisclosed = await get('logTrips', 'isBackgroundGeolocationDisclosed');
  return isDisclosed ?? false;
};

export const dismissBackgroundGeolocationDisclosed = () => {
  setBackgroundGeolocationDisclosed(false);
};

export const initialize = async () => {
  // If we're not in capacitor, then there is no way to use background geolocation
  if (!Capacitor.isNativePlatform()) {
    return;
  }
  try {
    await configureBackgroundGeolocation({ lowFrequencyTracking: false });
    // await configureBackgroundFetch();

    await checkProviderState();
  } catch (error) {
    // Client doesn't have plugin, probably on browser, disregard
    // eslint-disable-next-line no-console
    console.warn(error);
  }
};
